import "react-phone-input-2/lib/style.css";
import toast from "react-hot-toast";
import classes from "./AddCustomerAddress.module.css";
import PhoneInput from "react-phone-input-2";
import ModalWrapper from "../modalWrapper/ModalWrapper";
import MapboxComponent from "../mapboxComponent/MapboxComponent";
import { CloseIcon } from "../../images/deliveries/deliveriesModals/xIcon";
import { ActionBtn } from "../buttons/actionBtn/ActionBtn";
import { useLocation } from "react-router-dom";
import { InputElement } from "../inputElement/InputElement";
import { createCustomer } from "../../redux/customer/thunks/createCustomerThunk";
import { fetchCustomers } from "../../redux/customer/thunks/getCustomerThunk";
import { LoadingSpinner } from "../loadingSpinner/LoadingSpinner";
import { updateCustomer } from "../../redux/customer/thunks/updateCustomerThunk";
import { useStatusEffect } from "../../helperFunctions/customHooks/useStatusEffect";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { validateRequiredFields } from "../../helperFunctions/validateRequiredFields";
import { useDispatch, useSelector } from "react-redux";
import { resetCreateCustomerStatus } from "../../redux/customer/slices/createCustomerSlice";
import { resetUpdateCustomerStatus } from "../../redux/customer/slices/updateCustomerSlice";
import { useEffect, useRef, useState } from "react";
import {
  toggleAddNewCustomerAddress,
  setIsEditMode,
  setIsEditModalShown,
  setIsModalsBgShown,
  setAddressData,
  setSelectedAddress,
  setIsFetchCurrentAddress,
  setIsNewCustomerCreated,
  setSelectedDataForEdit,
} from "../../redux/globalFunctions/globalFnSlice";
import {
  resetCustomerForm,
  setCustomerForm,
} from "../../redux/forms/customerForm";

export function AddCustomerAddress() {
  const { isEditMode, selectedDataForEdit, addressData } = useSelector(
    (state) => state.global
  );
  const formData = useSelector((state) => state.forms.customerForm);
  const createCustomerState = useSelector((state) => state.createCustomer);
  const updateCustomerState = useSelector((state) => state.updateCustomer);

  const prevEditModeRef = useRef(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslations();

  const modalText = t.addCustomerModal;
  const validationText = t.formValidations;

  const [suggestions, setSuggestions] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [countryCode, setCountryCode] = useState("us");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [localPhone, setLocalPhone] = useState("");
  const [address, setAddress] = useState("");

  useEffect(() => {
    if (formData.address === "") {
      dispatch(setIsFetchCurrentAddress(false));
    } else {
      dispatch(setIsFetchCurrentAddress(true));
    }
  }, [formData.address]);

  function handleCloseModal() {
    if (
      location.pathname === "/deliveries" ||
      location.pathname === "/dashboard" ||
      location.pathname === "/scheduled-deliveries"
    ) {
      dispatch(setIsFetchCurrentAddress(false));
      dispatch(toggleAddNewCustomerAddress());
      dispatch(setIsModalsBgShown(true));
      dispatch(setIsEditModalShown(true));
      dispatch(setAddressData(""));

      // Restore edit mode if it was true before
      if (prevEditModeRef.current) {
        dispatch(setIsEditMode(true));
        prevEditModeRef.current = false;
      }
    } else if (isEditMode) {
      dispatch(toggleAddNewCustomerAddress());
      dispatch(resetCustomerForm());
      dispatch(setAddressData(""));
      setLocalPhone("");
      dispatch(setSelectedDataForEdit(null));
      dispatch(setIsFetchCurrentAddress(false));
    } else {
      dispatch(toggleAddNewCustomerAddress());
      dispatch(setAddressData(""));
    }
  }

  useEffect(() => {
    if (
      location.pathname === "/deliveries" ||
      location.pathname === "/dashboard" ||
      location.pathname === "/scheduled-deliveries"
    ) {
      // Save the current value of isEditMode before disabling it
      if (isEditMode) {
        prevEditModeRef.current = true;
      }
      dispatch(setIsEditMode(false));
    }
  }, [location.pathname, isEditMode, dispatch]);

  // Update address and coordinates when map is used
  useEffect(() => {
    if (addressData?.length !== 0) {
      // Filter addresses based on relevance
      const validAddresses = addressData.filter(
        (item) => item?.relevance >= 0.5
      );

      // Only show suggestions if there are valid addresses
      if (validAddresses.length > 0) {
        const suggestedAddresses = validAddresses.map(
          (item) => item.place_name
        );
        setSuggestions(suggestedAddresses);
      } else {
        setSuggestions([]);
      }

      const firstAddress = addressData[0];
      const geometry = firstAddress?.center;

      const latitude = geometry[1];
      const longitude = geometry[0];

      dispatch(
        setCustomerForm({
          latitude: latitude,
          longitude: longitude,
        })
      );
    } else {
      setSuggestions([]);
    }
  }, [addressData]);

  useEffect(() => {
    if (formData.address !== "" && addressData.length !== 0) {
      const context =
        addressData[isEditMode ? 0 : selectedIndex]?.context || [];
      const foundCity =
        context.find((entry) => entry.id.startsWith("place"))?.text || "";
      const foundCountry =
        context.find((entry) => entry.id.startsWith("country"))?.text || "";
      const postalCode =
        context.find((entry) => entry.id.startsWith("postcode"))?.text || "";

      dispatch(
        setCustomerForm({
          city: foundCity,
          country: foundCountry,
          postalCode: postalCode,
        })
      );
    }
  }, [formData.address, addressData]);

  const changeAddress = (newAddress, latitude, longitude) => {
    setAddress(newAddress);
    dispatch(
      setCustomerForm({
        address: newAddress,
        latitude: latitude,
        longitude: longitude,
      })
    );
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    dispatch(setCustomerForm({ [id]: value }));
  };

  // Handle form submission
  const handleFormSubmit = () => {
    const requiredFields = ["name", "email", "phone", "address"];

    const errors = validateRequiredFields(
      formData,
      requiredFields,
      validationText
    );

    if (errors.length > 0) {
      toast.dismiss();
      setTimeout(() => {
        errors.forEach((err) =>
          toast.error(err, {
            duration: 2000,
          })
        );
      }, 100);
      return;
    }

    if (isEditMode && location.pathname !== "/integrations") {
      dispatch(
        updateCustomer({
          c_id: selectedDataForEdit.c_id,
          ...formData,
        })
      );
    } else if (location.pathname === "/integrations") {
      const updatedFormData = {
        ...formData,
        dc_id: selectedDataForEdit?.dc_id,
        dc_custom_id: selectedDataForEdit?.dc_custom_id,
      };

      dispatch(createCustomer(updatedFormData));
    } else {
      dispatch(createCustomer(formData));
    }
  };

  useStatusEffect({
    status: createCustomerState.status,
    successMessage: modalText.successCreated,
    errorMessage: modalText.errorCreating + " " + createCustomerState.error,
    onSuccess: toggleAddNewCustomerAddress,
    reFetch: fetchCustomers,
    resetStatus: resetCreateCustomerStatus,
    onError: toggleAddNewCustomerAddress,
    resetForm: resetCustomerForm,
  });

  useStatusEffect({
    status: updateCustomerState.status,
    successMessage: modalText.successUpdating,
    errorMessage: modalText.errorUpdating + " " + updateCustomerState.error,
    onSuccess: toggleAddNewCustomerAddress,
    reFetch: fetchCustomers,
    resetStatus: resetUpdateCustomerStatus,
    onError: toggleAddNewCustomerAddress,
    resetForm: resetCustomerForm,
    resetSelectedDataForEdit: true,
    resetEditMode: true,
  });

  // Handler for createCustomer status
  useEffect(() => {
    if (createCustomerState.status === "success") {
      if (prevEditModeRef.current) {
        dispatch(setIsEditMode(true));
        prevEditModeRef.current = false;
      }
      if (
        location.pathname !== "/addresses" &&
        location.pathname !== "/integrations"
      ) {
        dispatch(setIsNewCustomerCreated(true));
      }
    }
  }, [createCustomerState.status, dispatch]);

  useEffect(() => {
    if (
      createCustomerState.status === "success" ||
      updateCustomerState.status === "success"
    ) {
      dispatch(setIsFetchCurrentAddress(false));
      setLocalPhone("");
    }
  }, [createCustomerState.status, updateCustomerState.status]);

  // If isEditMode is true then populate the fields with the data from selectedDataForEdit.
  useEffect(() => {
    if (isEditMode && selectedDataForEdit) {
      dispatch(
        setSelectedAddress(
          selectedDataForEdit.c_address || selectedDataForEdit.dc_address
        )
      );
      dispatch(
        setCustomerForm({
          name: selectedDataForEdit.c_name || selectedDataForEdit.dc_name || "",
          email:
            selectedDataForEdit.c_email || selectedDataForEdit.dc_email || "",
          address:
            selectedDataForEdit.c_address ||
            selectedDataForEdit.dc_address ||
            "",
          latitude: selectedDataForEdit.c_latitude || "",
          longitude: selectedDataForEdit.c_longitude || "",
        })
      );
    }
  }, [isEditMode, selectedDataForEdit]);

  useEffect(() => {
    if (selectedDataForEdit) {
      dispatch(
        setCustomerForm({
          phone:
            selectedDataForEdit.c_phonenumber ||
            selectedDataForEdit.dc_phonenumber ||
            "",
        })
      );
    }
  }, [countryCode]);

  const isStatusLoading =
    createCustomerState.status === "loading" ||
    updateCustomerState.status === "loading";

  useEffect(() => {
    if (
      (location.pathname === "/deliveries" ||
        location.pathname === "/dashboard" ||
        location.pathname === "/scheduled-deliveries") &&
      createCustomerState.status === "success"
    ) {
      setTimeout(() => {
        dispatch(setIsModalsBgShown(true));
        dispatch(setIsEditModalShown(true));
      }, 2000);
    }
  }, [createCustomerState.status]);

  const headerText = isEditMode ? modalText.editText : modalText.addText;

  // Prevent space from being added.
  const handleKeyPress = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  function onAddressClick(item, index) {
    setSelectedIndex(index);
    dispatch(setSelectedAddress(item));
    dispatch(setCustomerForm({ address: item }));
    setHasSearched(false);
  }

  // Set country code for the phone input field.
  useEffect(() => {
    if (formData.address && addressData.length !== 0) {
      if (addressData[0].properties.short_code) {
        const code = addressData[0]?.properties?.short_code;
        const countryCode = code.split("-")[0];
        setCountryCode(countryCode);
        setTimeout(() => {
          setLocalPhone(formData.phone);
        }, 100);
        dispatch(setCustomerForm({ countryCode: countryCode }));
      } else if (addressData[0].context) {
        const countryContext = addressData[0]?.context.find((entry) =>
          entry.id.startsWith("country")
        );
        if (countryContext) {
          const shortCode = countryContext.short_code;
          setCountryCode(shortCode);
          dispatch(setCustomerForm({ countryCode: shortCode }));
          setTimeout(() => {
            setLocalPhone(formData.phone);
          }, 100);
        } else {
          dispatch(setCustomerForm({ countryCode: "us" }));
          setCountryCode("us");
        }
      } else {
        setCountryCode("us");
        dispatch(setCustomerForm({ countryCode: "us" }));
      }
    } else {
      return;
    }
  }, [formData.address, addressData]);

  function handleCloseDropdowns() {
    setHasSearched(false);
  }

  function handleFocusAddress() {
    if (suggestions.length !== 0 && address !== "") {
      setHasSearched(true);
    }
  }

  function handleAddressOnChange(e) {
    dispatch(setSelectedAddress(null));
    setAddress(e.target.value);
  }

  function handleSearchClick() {
    if (address === "") return;
    setHasSearched(true);
    dispatch(setCustomerForm({ address: address }));
  }

  function handleResetForm() {
    dispatch(setIsFetchCurrentAddress(false));
    dispatch(resetCustomerForm());
    setLocalPhone("");
  }

  function onKeyDownSearch(e) {
    if (e.key === "Enter" && address !== "") {
      e.preventDefault();
      dispatch(setCustomerForm({ address: address }));
      setTimeout(() => {
        setHasSearched(true);
      }, 100);
    }
  }

  useEffect(() => {
    if (address === "") {
      setHasSearched(false);
    }
  }, [address]);

  const resetButton = [
    <ActionBtn
      text={t.addDeliveryModal.reset}
      onClick={handleResetForm}
      disabled={isEditMode}
    />,
  ];

  return (
    <ModalWrapper
      onClick={handleCloseModal}
      title={headerText}
      resetButton={resetButton}
    >
      <form className={classes.form}>
        <InputElement
          id={"name"}
          labelText={modalText.form.customer}
          onChange={handleInputChange}
          value={formData.name || ""}
          type={"text"}
          name={"name"}
        />
        <InputElement
          id={"email"}
          labelText={modalText.form.email}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          value={formData.email || ""}
          type={"email"}
          name={"email"}
        />

        <InputElement
          id={"country"}
          labelText={modalText.form.country}
          disabled={true}
          readOnly={true}
          value={formData.country || ""}
          type={"text"}
          name={"country"}
        />
        <InputElement
          id={"city"}
          labelText={modalText.form.city}
          disabled={true}
          readOnly={true}
          value={formData.city || ""}
          type={"text"}
          name={"city"}
        />
        <InputElement
          id={"postalCode"}
          labelText={modalText.form.postalCode}
          disabled={true}
          readOnly={true}
          value={formData.postalCode || ""}
          type={"text"}
          name={"postalCode"}
        />

        <div className={classes.formElementAddress}>
          <div className={classes.searchContainer}>
            <div style={{ width: "100%" }}>
              <label htmlFor="address">{modalText.form.address}</label>
              <input
                type="text"
                id="address"
                value={address || ""}
                onFocus={handleFocusAddress}
                onChange={(e) => handleAddressOnChange(e)}
                onKeyDown={onKeyDownSearch}
                required
              />
            </div>
            <ActionBtn onClick={handleSearchClick} text={modalText.search} />
          </div>

          {suggestions.length !== 0 && address.length !== 0 && hasSearched && (
            <div className={classes.suggestionsContainer}>
              <button
                className={classes.closeBtn}
                onClick={handleCloseDropdowns}
                type="button"
              >
                <CloseIcon />
              </button>

              {suggestions.map((item, index) => (
                <span key={index} onClick={() => onAddressClick(item, index)}>
                  {item}
                </span>
              ))}
            </div>
          )}
          {suggestions.length === 0 && address.length !== 0 && hasSearched && (
            <span className={classes.noResultsFound}>
              {modalText.noResultsFound}
            </span>
          )}
        </div>
        <div className={classes.formElement}>
          <label htmlFor="phonenumber">{modalText.form.phone}</label>
          <PhoneInput
            country={formData.countryCode.toLocaleLowerCase()}
            value={localPhone}
            onChange={(value) => {
              setLocalPhone(value);
              dispatch(setCustomerForm({ phone: value }));
            }}
            inputProps={{
              name: "phone",
              required: true,
            }}
            containerClass="phone-input-container"
            inputClass="phone-input"
            countryCodeEditable={false}
            enableSearch={true}
          />
        </div>

        <MapboxComponent
          address={formData.address || ""}
          city={formData.address || ""}
          country={formData.address || ""}
          updateAddress={changeAddress}
        />
      </form>

      <ActionBtn
        width={"100%"}
        height={"42px"}
        onClick={handleFormSubmit}
        text={isStatusLoading ? <LoadingSpinner /> : modalText.saveBtn}
        disabled={isStatusLoading}
      />
    </ModalWrapper>
  );
}
